import React, { useCallback } from 'react';
import { useHover } from '@landr/maestro';
import { analytics } from 'utils/analytics';
import { getPackRouteEntityPath } from 'helpers';
import { useGenericCarouselContext } from 'components/GenericCarousel';
import { CommonCard, CommonCardProps } from 'types';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { withInteractionSource } from 'contexts/InteractionSourceContext';
import { useTakenDownPack } from 'hooks/useTakenDown/useTakenDown';
import { useDownloadsPageMatch } from 'hooks/useDownloadsPageMatch/useDownloadsPageMatch';
import useDetectTargetIsInsideViewport from 'hooks/useDetectTargetIsInsideViewport/useDetectTargetIsInsideViewport';
import { Pack, Repack } from 'types/generated/graphql';
import PackPreviewCard from '../PackPreviewCard/PackPreviewCard';

export interface PackCardProps extends CommonCardProps<Pack | Repack> {
    page?: number;
    position?: number;
}

const PackCardComponent: CommonCard<Pack | Repack> = ({ item }) => {
    const [hoverRef, isHovered] = useHover(false);
    const isInViewport = useDetectTargetIsInsideViewport({ ref: hoverRef });

    const { carouselName } = useGenericCarouselContext();

    const { isTakenDown } = useTakenDownPack(item);
    const { isDownloadsPage } = useDownloadsPageMatch();
    const isTakenDownUIVisible = isTakenDown && isDownloadsPage;

    const entityPath = getPackRouteEntityPath(item);

    const to = `/${entityPath}/${item.slug}`;

    const onCardClick = useCallback(() => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.ViewDetail,
            ctaType: CtaType.Card,
            interactionSource: InteractionSource.CardAction,
            ctaText: carouselName
                ? `${carouselName} - ${item.name}`
                : item.name,
            ctaDestination: to,
        });
    }, [carouselName, item.name, to]);

    return (
        <PackPreviewCard
            ref={hoverRef}
            isExclusive={item.isExclusive}
            backgroundColor={item.backgroundColor}
            linkTo={to}
            bylineLinkTo={to}
            title={item.name}
            byline={undefined}
            item={item}
            artworkUrl={item.artworkUrl}
            isTakenDownUIVisible={isTakenDownUIVisible}
            onLinkClick={onCardClick}
            isHovered={isHovered}
            isLoading={!isInViewport}
        />
    );
};

export const PackCard = withInteractionSource(
    PackCardComponent,
    InteractionSource.CardAction,
);
